import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { CalendarIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";

import api from "api";
import util from "utils/utils";

import CircularProgress from "components/lib/CircularProgress";
import ProductTour from "components/lib/ProductTour";

import {
  HomeBlockActions,
  HomeRow,
  HomeRowActions,
  HomeRowContainer,
  HomeRowContent,
} from "../HomeUI";
import { getDateStatus } from "../dateStatus";
import HomeBlock from "./HomeBlock";

type AssessmentsBlockProps = { variant?: "pending" | "completed" };

const AssessmentsBlock = ({ variant = "pending" }: AssessmentsBlockProps) => {
  const [showMore, setShowMore] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const assessmentQuery = useQuery(initUseQuery(api.users.dashboard, []));
  const isPending = variant === "pending";

  const assignments = isPending
    ? (assessmentQuery.data?.assessmentAssignments ?? [])
    : (assessmentQuery.data?.completedAssessmentAssignments ?? []);
  const assessmentCount = assignments.reduce(
    (acc, challenge) =>
      acc +
      (isPending
        ? challenge.incompleteAssessmentCount
        : (challenge.completedAssessmentCount ?? 0)),
    0,
  );

  if (!assessmentCount) return null;

  const countPerColumn = 3;
  const visibleAssessmentAssignments = assignments.slice(
    0,
    showMore ? assignments.length : countPerColumn * 2,
  );
  const columns = visibleAssessmentAssignments.length > countPerColumn ? 2 : 1;

  return (
    <HomeBlock $width={columns}>
      {isPending ? <ProductTour tour="assessmentHomepage" isReady /> : null}
      <Label color={isPending ? "orange" : "green"} size="small">
        {t(
          isPending
            ? "home.assessments.waiting.label"
            : "home.assessments.completed.label",
        )}
      </Label>
      <H3>
        {t(
          isPending
            ? "home.assessments.waiting.title"
            : "home.assessments.completed.title",
        )}
        <Subheader>
          {t(
            isPending
              ? "home.assessments.waiting.info"
              : "home.assessments.completed.info",
            { count: assessmentCount, challenges: assignments.length },
          )}
        </Subheader>
      </H3>
      <HomeRowContainer
        $columns={columns}
        className={isPending ? "assessment-homepage-tour-step-1" : undefined}
      >
        {visibleAssessmentAssignments.map((challenge, index) => {
          const completed = challenge.completedAssessmentCount ?? 0;
          const total =
            challenge.totalAssessmentCount ??
            completed + challenge.incompleteAssessmentCount;
          const progress = total > 0 ? completed / total : 0;
          const deadlineStatus = getDateStatus(challenge.assessmentDeadline);

          return (
            <HomeRow
              key={challenge._id}
              className={
                isPending && index === 0
                  ? "assessment-homepage-tour-step-2"
                  : undefined
              }
            >
              <CircularProgress
                size={44}
                progress={progress}
                label={`${completed}/${total}`}
                color={theme.colors.primary.solid}
                backgroundColor={theme.colors.primary.soft}
              />
              <HomeRowContent>
                <H4>
                  {challenge.name}
                  <Subheader>
                    <p>
                      {isPending
                        ? t("home.assessments.waiting.remaining", {
                            assessments: util.pluralise(
                              challenge.incompleteAssessmentCount,
                              "assessment",
                              "assessments",
                            ),
                          })
                        : t("home.assessments.completed.remaining", {
                            assessments: util.pluralise(
                              completed,
                              "assessment",
                              "assessments",
                            ),
                          })}
                    </p>
                    <p>
                      {deadlineStatus ? (
                        <Label
                          color={deadlineStatus.color}
                          basic={deadlineStatus.basic}
                          size="small"
                        >
                          <Icon icon={CalendarIcon} />
                          Deadline: {deadlineStatus.text}
                        </Label>
                      ) : null}
                    </p>
                  </Subheader>
                </H4>
              </HomeRowContent>
              <HomeRowActions>
                <ButtonLink
                  color="primary"
                  variant="soft"
                  size="small"
                  to={`/challenges/${challenge._id}/assessments/assigned`}
                >
                  {t(
                    isPending
                      ? "home.assessments.waiting.action"
                      : "home.assessments.completed.action",
                  )}
                </ButtonLink>
              </HomeRowActions>
            </HomeRow>
          );
        })}
      </HomeRowContainer>
      {assignments.length > countPerColumn * 2 ? (
        <HomeBlockActions>
          <Button
            color="primary"
            variant="soft"
            size="small"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore
              ? t("home.assessments.hide")
              : t("home.assessments.viewMore")}
          </Button>
        </HomeBlockActions>
      ) : null}
    </HomeBlock>
  );
};

export default AssessmentsBlock;
